import { useEffect, useMemo, useState, Fragment } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Settings2, Plus, Trash2, Save, Loader2, Shield, UserCog, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { api, errMsg, fmtDate } from "@/lib/api";
import { useMe } from "@/lib/me";
import { PageHeader, Pill, RoleBadge } from "@/components/app/shared";
import { cn } from "@/lib/utils";

/* -------------------- PROFILE -------------------- */
function Profile() {
  const me = useMe();
  const qc = useQueryClient();
  const [name, setName] = useState(me.name || "");
  const [title, setTitle] = useState(me.title || "");
  const save = useMutation({
    mutationFn: async () => (await api.patch("/me", { name, title })).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["me"] }); toast.success("Perfil actualizado"); },
    onError: (e) => toast.error(errMsg(e)),
  });
  return (
    <section className="rounded-lg border border-border bg-card p-6">
      <div className="kicker">Tu perfil</div>
      <div className="mt-4 grid gap-4 md:grid-cols-3">
        <div className="grid gap-1.5"><Label>Nombre</Label><Input value={name} onChange={(e) => setName(e.target.value)} data-testid="profile-name-input" /></div>
        <div className="grid gap-1.5"><Label>Cargo</Label><Input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="profile-title-input" /></div>
        <div className="grid gap-1.5"><Label>Correo y rol</Label>
          <div className="flex h-9 items-center gap-2"><span className="truncate font-mono text-xs">{me.email}</span><RoleBadge role={me.role} /></div></div>
      </div>
      <Button className="mt-5" onClick={() => save.mutate()} disabled={save.isPending} data-testid="profile-save-button">Guardar perfil</Button>
    </section>
  );
}

/* -------------------- USER PERMISSIONS DRAWER -------------------- */
function UserPermissionsDrawer({ user, catalog, rolesByKey, open, onOpenChange }) {
  const qc = useQueryClient();
  const roleDoc = rolesByKey[user?.role];
  const rolePerms = useMemo(() => new Set(roleDoc?.permissions || []), [roleDoc]);
  const isSuper = user?.role === "SUPERADMIN";
  const [overrides, setOverrides] = useState({}); // key -> true|false (missing = default)

  // Reset overrides when switching user
  useEffect(() => {
    setOverrides({ ...(user?.permissionOverrides || {}) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.user_id, open]);

  const effective = (key) => {
    if (overrides[key] === true) return true;
    if (overrides[key] === false) return false;
    return rolePerms.has("*") || rolePerms.has(key);
  };
  const toggle = (key) => {
    const roleHas = rolePerms.has("*") || rolePerms.has(key);
    const cur = overrides[key];
    // tri-state: default → true → false → default
    let next;
    if (cur === undefined) next = roleHas ? false : true;
    else if (cur === true) next = false;
    else if (cur === false) next = undefined;
    setOverrides((o) => { const n = { ...o }; if (next === undefined) delete n[key]; else n[key] = next; return n; });
  };
  const reset = (key) => setOverrides((o) => { const n = { ...o }; delete n[key]; return n; });

  const save = useMutation({
    mutationFn: async () => {
      const payload = {};
      Object.keys({ ...(user?.permissionOverrides || {}), ...overrides }).forEach((k) => {
        payload[k] = overrides[k] === undefined ? null : overrides[k];
      });
      return (await api.patch(`/users/${user.user_id}/permissions`, { overrides: payload })).data;
    },
    onSuccess: () => { toast.success("Permisos actualizados"); qc.invalidateQueries({ queryKey: ["users"] }); qc.invalidateQueries({ queryKey: ["me"] }); onOpenChange(false); },
    onError: (e) => toast.error(errMsg(e)),
  });

  if (!user) return null;
  const groups = (() => {
    const g = {};
    (catalog?.permissions || []).forEach((p) => { (g[p.group] = g[p.group] || []).push(p); });
    return g;
  })();

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full overflow-y-auto bg-card sm:max-w-xl" data-testid="user-permissions-drawer">
        <SheetHeader>
          <div className="kicker">Permisos del usuario</div>
          <SheetTitle className="font-display text-4xl font-normal">{user.name || user.email}</SheetTitle>
          <SheetDescription>
            Hereda los permisos del rol <b>{roleDoc?.name || user.role}</b>. Puedes añadir o quitar permisos puntuales como excepción.
          </SheetDescription>
        </SheetHeader>

        {isSuper && (
          <div className="mt-4 rounded-md border border-lilac bg-lilac/40 px-4 py-3 text-xs">
            Superadmin siempre tiene todo. Los permisos no son editables.
          </div>
        )}

        <div className="mt-6 space-y-6">
          {Object.entries(groups).map(([group, items]) => (
            <section key={group}>
              <div className="kicker mb-2">{group}</div>
              <div className="space-y-2">
                {items.map((p) => {
                  const roleHas = rolePerms.has("*") || rolePerms.has(p.key);
                  const override = overrides[p.key];
                  const effVal = effective(p.key);
                  return (
                    <div key={p.key} className="flex items-center justify-between gap-3 rounded-md border border-border bg-background px-3 py-2.5" data-testid={`user-perm-row-${p.key}`}>
                      <div className="min-w-0">
                        <div className="text-sm">{p.label}</div>
                        <div className="mt-0.5 flex items-center gap-1.5 text-[11px] font-mono text-muted-foreground">
                          <span>{p.key}</span>
                          <span>·</span>
                          <span>Rol: {roleHas ? "✓ permitido" : "✗ no"}</span>
                          {override !== undefined && <Pill tone={override ? "mint" : "blush"} className="ml-1">excepción</Pill>}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={!!effVal}
                          onCheckedChange={() => !isSuper && toggle(p.key)}
                          disabled={isSuper}
                          data-testid={`user-perm-switch-${p.key}`}
                        />
                        {!isSuper && override !== undefined && (
                          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => reset(p.key)} aria-label="Quitar excepción"><RefreshCw className="h-3.5 w-3.5" /></Button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          ))}
        </div>

        {!isSuper && (
          <div className="sticky bottom-0 mt-6 flex gap-2 border-t border-border bg-card py-4">
            <Button variant="outline" onClick={() => onOpenChange(false)} className="flex-1" data-testid="user-perm-cancel">Cancelar</Button>
            <Button onClick={() => save.mutate()} disabled={save.isPending} className="flex-1" data-testid="user-perm-save">
              {save.isPending ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Save className="mr-1.5 h-4 w-4" />} Guardar permisos
            </Button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

/* -------------------- USERS LIST -------------------- */
function UsersTab({ catalog, rolesByKey }) {
  const me = useMe();
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["users"], queryFn: async () => (await api.get("/users")).data });
  const [editUser, setEditUser] = useState(null);
  const setRole = useMutation({
    mutationFn: async ({ user_id, role }) => (await api.patch(`/users/${user_id}/role`, { role })).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["users"] }); qc.invalidateQueries({ queryKey: ["me"] }); toast.success("Rol actualizado"); },
    onError: (e) => toast.error(errMsg(e)),
  });

  return (
    <>
      <section className="rounded-lg border border-border bg-card">
        <div className="flex items-center justify-between border-b border-border px-5 py-4">
          <div>
            <div className="text-sm font-semibold">Equipo</div>
            <div className="text-xs text-muted-foreground">Las personas aparecen aquí tras su primer inicio de sesión con un correo @medianamag.com. Nuevos usuarios entran como Editorial.</div>
          </div>
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="text-xs uppercase tracking-wider">Persona</TableHead>
              <TableHead className="text-xs uppercase tracking-wider">Cargo</TableHead>
              <TableHead className="text-xs uppercase tracking-wider">Alta</TableHead>
              <TableHead className="w-[180px] text-xs uppercase tracking-wider">Rol</TableHead>
              <TableHead className="w-[200px] text-xs uppercase tracking-wider">Permisos</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && <TableRow><TableCell colSpan={5} className="text-sm text-muted-foreground">Cargando…</TableCell></TableRow>}
            {data?.users.map((u) => {
              const overrides = Object.keys(u.permissionOverrides || {}).length;
              return (
                <TableRow key={u.user_id} data-testid="team-user-row">
                  <TableCell>
                    <div className="text-sm font-medium">{u.name}</div>
                    <div className="font-mono text-[11px] text-muted-foreground">{u.email}</div>
                  </TableCell>
                  <TableCell className="text-sm">{u.title || "—"}</TableCell>
                  <TableCell className="font-mono text-xs">{fmtDate(u.createdAt, { day: "2-digit", month: "short", year: "numeric" })}</TableCell>
                  <TableCell>
                    <Select value={u.role} onValueChange={(role) => setRole.mutate({ user_id: u.user_id, role })}>
                      <SelectTrigger className="h-8" data-testid={`team-role-select-${u.email}`}><SelectValue /></SelectTrigger>
                      <SelectContent>{data.rolesFull.map((r) => <SelectItem key={r.key} value={r.key}>{r.name}</SelectItem>)}</SelectContent>
                    </Select>
                    {u.user_id === me.user_id && <div className="mt-1 text-[10px] text-muted-foreground">tú</div>}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Button size="sm" variant="outline" onClick={() => setEditUser(u)} data-testid={`user-edit-permissions-${u.email}`}>
                        <UserCog className="mr-1.5 h-3.5 w-3.5" /> Editar
                      </Button>
                      {overrides > 0 && <Pill tone="butter" data-testid="user-overrides-count">{overrides} excepción{overrides !== 1 ? "es" : ""}</Pill>}
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </section>
      <UserPermissionsDrawer user={editUser} catalog={catalog} rolesByKey={rolesByKey} open={!!editUser} onOpenChange={(o) => !o && setEditUser(null)} />
    </>
  );
}

/* -------------------- ROLES & PERMISSIONS -------------------- */
function RoleEditor({ role, catalog, onClose }) {
  const qc = useQueryClient();
  const isNew = !role?.id;
  const [name, setName] = useState(role?.name || "");
  const [description, setDescription] = useState(role?.description || "");
  const [permSet, setPermSet] = useState(() => new Set(role?.permissions || []));
  const locked = role?.systemLocked;

  const toggle = (key) => setPermSet((s) => { const n = new Set(s); n.has(key) ? n.delete(key) : n.add(key); return n; });

  const save = useMutation({
    mutationFn: async () => {
      const body = { name, description, permissions: [...permSet] };
      if (isNew) return (await api.post("/roles", body)).data;
      return (await api.patch(`/roles/${role.id}`, body)).data;
    },
    onSuccess: () => { toast.success(isNew ? "Rol creado" : "Rol actualizado"); qc.invalidateQueries({ queryKey: ["roles-catalog"] }); qc.invalidateQueries({ queryKey: ["users"] }); qc.invalidateQueries({ queryKey: ["me"] }); onClose(); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const del = useMutation({
    mutationFn: async () => api.delete(`/roles/${role.id}`),
    onSuccess: () => { toast.success("Rol eliminado"); qc.invalidateQueries({ queryKey: ["roles-catalog"] }); qc.invalidateQueries({ queryKey: ["users"] }); onClose(); },
    onError: (e) => toast.error(errMsg(e)),
  });

  const groups = useMemo(() => {
    const g = {};
    (catalog?.permissions || []).forEach((p) => { (g[p.group] = g[p.group] || []).push(p); });
    return g;
  }, [catalog]);

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto bg-card" data-testid="role-editor-dialog">
        <DialogHeader>
          <div className="kicker">{isNew ? "Nuevo rol" : role.builtin ? "Rol predeterminado" : "Rol personalizado"}</div>
          <DialogTitle className="font-display text-3xl font-normal">{isNew ? "Crear rol" : role.name}</DialogTitle>
          <DialogDescription>
            {locked ? "Superadmin tiene todos los permisos y no se puede modificar." : "Define el nombre del rol y marca los permisos que tendrá por defecto. Cada usuario puede recibir excepciones."}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid gap-3 md:grid-cols-2">
            <div className="grid gap-1.5"><Label className="text-xs">Nombre del rol</Label>
              <Input value={name} disabled={locked} onChange={(e) => setName(e.target.value)} data-testid="role-name-input" /></div>
            <div className="grid gap-1.5"><Label className="text-xs">Clave interna</Label>
              <Input value={role?.key || "— (se asigna al crear)"} disabled className="font-mono text-xs" /></div>
          </div>
          <div className="grid gap-1.5"><Label className="text-xs">Descripción (opcional)</Label>
            <Textarea rows={2} value={description} disabled={locked} onChange={(e) => setDescription(e.target.value)} data-testid="role-description-input" /></div>

          <Separator />

          <div className="grid gap-5">
            {Object.entries(groups).map(([group, items]) => (
              <section key={group}>
                <div className="mb-2 flex items-center justify-between">
                  <div className="kicker">{group}</div>
                  {!locked && (
                    <div className="flex gap-1.5">
                      <Button size="sm" variant="ghost" className="h-7 text-[11px]" onClick={() => setPermSet((s) => { const n = new Set(s); items.forEach((i) => n.add(i.key)); return n; })} data-testid={`role-enable-group-${group}`}>Todos</Button>
                      <Button size="sm" variant="ghost" className="h-7 text-[11px]" onClick={() => setPermSet((s) => { const n = new Set(s); items.forEach((i) => n.delete(i.key)); return n; })} data-testid={`role-disable-group-${group}`}>Ninguno</Button>
                    </div>
                  )}
                </div>
                <div className="grid gap-2 md:grid-cols-2">
                  {items.map((p) => {
                    const on = locked || permSet.has(p.key);
                    return (
                      <label key={p.key} className={cn("flex cursor-pointer items-center justify-between gap-2 rounded-md border border-border bg-background px-3 py-2.5", locked && "cursor-not-allowed opacity-70")} data-testid={`role-perm-${p.key}`}>
                        <div className="min-w-0">
                          <div className="text-sm">{p.label}</div>
                          <div className="font-mono text-[10px] text-muted-foreground">{p.key}</div>
                        </div>
                        <Switch checked={on} disabled={locked} onCheckedChange={() => toggle(p.key)} data-testid={`role-perm-switch-${p.key}`} />
                      </label>
                    );
                  })}
                </div>
              </section>
            ))}
          </div>
        </div>
        <div className="flex items-center justify-between gap-2 border-t border-border pt-4">
          <div>
            {!isNew && !role.builtin && (
              <Button variant="outline" size="icon" onClick={() => del.mutate()} disabled={del.isPending} aria-label="Eliminar rol" data-testid="role-delete-button"><Trash2 className="h-4 w-4" /></Button>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>Cerrar</Button>
            {!locked && (
              <Button onClick={() => save.mutate()} disabled={!name.trim() || save.isPending} data-testid="role-save-button">
                {save.isPending ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Save className="mr-1.5 h-4 w-4" />} Guardar rol
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function RolesTab({ catalog }) {
  const roles = catalog?.roles || [];
  const [editing, setEditing] = useState(null);
  const groups = useMemo(() => {
    const g = {};
    (catalog?.permissions || []).forEach((p) => { (g[p.group] = g[p.group] || []).push(p); });
    return g;
  }, [catalog]);

  const roleHas = (r, key) => (r.permissions || []).includes("*") || (r.permissions || []).includes(key);

  return (
    <>
      <section className="rounded-lg border border-border bg-card">
        <div className="flex items-center justify-between border-b border-border px-5 py-4">
          <div>
            <div className="text-sm font-semibold">Roles & permisos</div>
            <div className="text-xs text-muted-foreground">Define qué puede hacer cada rol. Los cambios se aplican a todas las personas del rol salvo que tengan excepciones.</div>
          </div>
          <Button onClick={() => setEditing({ permissions: [] })} data-testid="role-new-button"><Plus className="mr-1.5 h-4 w-4" /> Nuevo rol</Button>
        </div>

        {/* Matrix */}
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm" data-testid="roles-matrix">
            <thead>
              <tr className="border-b border-border">
                <th className="sticky left-0 z-10 bg-card px-4 py-3 text-left text-[11px] uppercase tracking-wider min-w-[220px]">Permiso</th>
                {roles.map((r) => (
                  <th key={r.key} className="px-3 py-3 text-center text-[11px] uppercase tracking-wider whitespace-nowrap">
                    <div className="flex flex-col items-center gap-1">
                      <span>{r.name}</span>
                      {r.systemLocked ? <Pill tone="lilac">bloqueado</Pill> : r.builtin ? <Pill tone="neutral">predet.</Pill> : <Pill tone="mint">personalizado</Pill>}
                    </div>
                  </th>
                ))}
                <th className="px-3 py-3 w-10"></th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(groups).map(([group, items]) => (
                <Fragment key={group}>
                  <tr className="bg-surface-2">
                    <td colSpan={roles.length + 2} className="px-4 py-2 font-mono text-[10.5px] uppercase tracking-wider text-muted-foreground">{group}</td>
                  </tr>
                  {items.map((p) => (
                    <tr key={p.key} className="border-b border-border" data-testid={`roles-matrix-row-${p.key}`}>
                      <td className="sticky left-0 z-10 bg-card px-4 py-2.5">
                        <div className="text-sm">{p.label}</div>
                        <div className="font-mono text-[10px] text-muted-foreground">{p.key}</div>
                      </td>
                      {roles.map((r) => (
                        <td key={r.key} className="px-3 py-2.5 text-center" data-testid={`roles-matrix-cell-${r.key}-${p.key}`}>
                          {roleHas(r, p.key) ? (
                            <Pill tone="mint" className="font-mono text-[10px]">sí</Pill>
                          ) : (
                            <span className="text-muted-foreground font-mono text-[10px]">—</span>
                          )}
                        </td>
                      ))}
                      <td className="px-2"></td>
                    </tr>
                  ))}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>

        <div className="border-t border-border px-5 py-4 flex flex-wrap items-center gap-2">
          <span className="text-xs text-muted-foreground mr-2">Editar un rol:</span>
          {roles.map((r) => (
            <Button key={r.key} size="sm" variant="outline" onClick={() => setEditing(r)} data-testid={`role-edit-${r.key}`}>
              <Shield className="mr-1.5 h-3.5 w-3.5" /> {r.name}
            </Button>
          ))}
        </div>
      </section>
      {editing && <RoleEditor role={editing} catalog={catalog} onClose={() => setEditing(null)} />}
    </>
  );
}

/* -------------------- PAGE -------------------- */
export default function Team() {
  const me = useMe();
  const isSuper = me.role === "SUPERADMIN";
  const { data: catalog, isLoading } = useQuery({
    queryKey: ["roles-catalog"],
    queryFn: async () => (await api.get("/permissions/catalog")).data,
  });
  const rolesByKey = useMemo(() => {
    const m = {};
    (catalog?.roles || []).forEach((r) => { m[r.key] = r; });
    return m;
  }, [catalog]);

  return (
    <div className="space-y-8">
      <PageHeader kicker="07 · Equipo & Ajustes" title="Equipo & Ajustes" subtitle="Perfil personal, gestión del equipo y configuración granular de roles y permisos." />
      <Tabs defaultValue="profile">
        <TabsList>
          <TabsTrigger value="profile" data-testid="team-tab-profile">Perfil</TabsTrigger>
          {isSuper && <TabsTrigger value="users" data-testid="team-tab-users">Equipo</TabsTrigger>}
          {isSuper && <TabsTrigger value="roles" data-testid="team-tab-roles">Roles & permisos</TabsTrigger>}
        </TabsList>
        <TabsContent value="profile" className="mt-6">
          <Profile />
        </TabsContent>
        {isSuper && (
          <TabsContent value="users" className="mt-6">
            {isLoading ? <Skeleton className="h-64" /> : <UsersTab catalog={catalog} rolesByKey={rolesByKey} />}
          </TabsContent>
        )}
        {isSuper && (
          <TabsContent value="roles" className="mt-6">
            {isLoading ? <Skeleton className="h-64" /> : <RolesTab catalog={catalog} />}
          </TabsContent>
        )}
      </Tabs>
      {!isSuper && (
        <div className="rounded-lg border border-dashed border-border bg-surface-2 p-6 text-sm text-muted-foreground" data-testid="team-non-admin-note">
          La gestión de equipo, roles y permisos está reservada a Superadmin.
        </div>
      )}
    </div>
  );
}
